import { ComponentPropsWithoutRef, useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { generatePath, Link, useHistory } from "react-router-dom";
import logo from "../../../assets/logos/Myloc-logga.svg";
import { useTranslate } from "../../../language/i18n";
import { RootState } from "../../../reducers/rootReducer";
import { selectHasComflowAccess, selectHasFSPAccess } from "../../../reducers/session/sessionSelectors";
import { SearchResult } from "../../../services/search/dataTypes";
import SearchService from "../../../services/search/SearchService";
import pages from "../../../utils/pages";
import { useTablet } from "../../../utils/viewport";
import LinkToComflow from "../../Comflow/LinkToComflow";
import MessageButton from "../../Message/MessageButton";
import QRCodeButton from "../../shared/Button/QRCodeButton";
import LogoutButton from "../../shared/Buttons/LogoutButton";
import { CONTAINED } from "../../shared/Buttons/MuiButton/MuiButton";
import ScannerDialog from "../../shared/scanner/ScannerDialog";
import CartButton from "./CartButton/CartButton";
import styles from "./Header.module.scss";
import Menu from "./Menu/Menu";
import MyMylocMenu from "./MyMylocMenu/MyMylocMenu";
import OrderMenu from "./OrderMenu/OrderMenu";
import ReceiverMenu from "./ReceiverMenu/ReceiverMenu";

function Header() {
  const isDesktop = !useTablet();
  const userLoggedIn = !!useSelector((state: RootState) => state.session.sessionId);
  const order = useSelector((state: RootState) => state.appData.order);
  const hasFSPAccess = useSelector(selectHasFSPAccess);
  const hasComflowAccess = useSelector(selectHasComflowAccess);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const history = useHistory();

  const translate = useTranslate();

  const closeScanner = useCallback(() => {
    setScannerOpen(false);
  }, []);

  const handleSearchResult = useCallback(
    (searchResult: SearchResult) => {
      const type = searchResult.type;
      const id = searchResult.id;

      if (type === "Item") {
        history.push({
          pathname: generatePath(pages.PRODUCT_DETAIL.PATH, {
            product: id,
          }),
        });
      } else {
        history.push({
          pathname: generatePath(pages.FIXED_ASSET_INVENTORY.PATH, {
            product: id,
          }),
        });
      }
    },
    [history],
  );

  const handleScan = useCallback<ComponentPropsWithoutRef<typeof ScannerDialog>["onScan"]>(
    async scannedBarcodes => {
      if (scannedBarcodes.length === 0) return;

      setIsLoading(true);

      const result = await SearchService.getSearch(scannedBarcodes[0].rawValue);

      if (result.isOk() && result.data != null) {
        handleSearchResult(result.data as SearchResult);
      }

      setIsLoading(false);
      closeScanner();
    },
    [closeScanner, handleSearchResult],
  );

  const showLinkInHeader = () => {
    return hasComflowAccess && (isDesktop || !hasFSPAccess);
  };

  return (
    <header className={styles.header}>
      <div className={styles.headerContent}>
        <div className={styles.containerLeft}>
          <h1 className={styles.hidden}>{translate("SITE_TITLE")}</h1>
          <Link to={pages.HOME}>
            <img src={logo} alt="Myloc logo" className={styles.logo} />
          </Link>
          {showLinkInHeader() && <LinkToComflow />}
        </div>
        {userLoggedIn && (
          <div className={styles.rightMenu}>
            {hasFSPAccess ? (
              <>
                <QRCodeButton onClick={() => setScannerOpen(true)} />
                <MessageButton />
                <MyMylocMenu />
                {order?.receiver && <ReceiverMenu />}
                <OrderMenu />
                <CartButton />
                <ScannerDialog
                  onScan={handleScan}
                  open={scannerOpen}
                  handleClose={closeScanner}
                  isLoading={isLoading}
                />
              </>
            ) : (
              hasComflowAccess && <LogoutButton widthPX={150} variant={CONTAINED} />
            )}
          </div>
        )}
      </div>
      <Menu userLoggedIn={userLoggedIn} />
    </header>
  );
}

export default Header;
